import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ui/confirm';
import {
  getGetModerationHistoryQueryKey,
  getGetReportQueryKey,
  useCreateModerationHistory,
  useDeleteComment,
  useDismissReport,
  useReopenReport,
  useSaveReportNote,
  useSendFeedbackEmail,
} from '@/api/generated/moderation/moderation';
import { useChangeUserStatus } from '@/api/generated/user-manager/user-manager';
import { useUpdatePostStatus } from '@/api/generated/post-manager/post-manager';
import type { ModerationDetail } from '../types';
import { MODERATION_LIST_KEY, MODERATION_INTELLIGENCE_KEY, useDeleteReportedMessage, useSendModerationNotification, useModerationOverride } from '../api';
import { runReversible } from '../actions/runReversible';

// Status codes for the cross-controller actions (doc 09 §3).
const USER_STATUS_BAN = 'TER';
const USER_STATUS_UNBAN = 'ACT';
const POST_STATUS_HIDE = 'HID';
const POST_STATUS_LIST = 'PUB';
// Content "hard delete" surrogate: mark the post permanently TERMINATED (parity with user ban=TER;
// same status the mobile app already treats as un-editable/removed — PostServiceImpl.java:803).
const CONTENT_STATUS_REMOVED = 'TER';

export interface ModerationActions {
  /** True while ANY moderation mutation is in flight — disables buttons to block double-submit. */
  isPending: boolean;
  dismiss: () => Promise<void>;
  reopen: () => Promise<void>;
  banUser: () => Promise<void>;
  unbanUser: () => Promise<void>;
  takeDown: () => Promise<void>;
  listContent: () => Promise<void>;
  deleteComment: () => Promise<void>;
  sendFeedbackEmail: () => Promise<void>;
  solveFeedback: () => Promise<void>;
  saveNote: (note: string) => Promise<void>;
  /** Destructive, real hard-delete: mutates the post/message then navigates back to the queue. */
  removeMessage: () => Promise<void>;
  removeContent: () => Promise<void>;
  /** Opens the Send Notification dialog (real submit is `submitNotification`). */
  sendNotification: () => Promise<void>;
  notifyDialogOpen: boolean;
  closeNotifyDialog: () => void;
  submitNotification: (title: string, message: string) => Promise<void>;
  /** Coop intelligence override actions (Phase 1). */
  forceApprove: () => Promise<void>;
  forceReject: () => Promise<void>;
  escalateGpu: () => Promise<void>;
  escalateHuman: () => Promise<void>;
}

/**
 * Every moderation action, wired behind useConfirm with the doc 09 §4.6 safety model:
 *   destructive -> blocking confirm + required reason, no undo;
 *   reversible   -> optimistic-with-undo (real inverse endpoint);
 *   sideEffect   -> confirm (+ message where required);
 *   positive     -> confirm (+ optional note).
 * Success AND error toasts on every path (CMP swallowed both — doc 09 §2.2). Report + history queries
 * are invalidated after each mutation; the list is invalidated on navigate-back.
 */
export function useModerationActions(report: ModerationDetail | null): ModerationActions {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const dismissMut = useDismissReport();
  const reopenMut = useReopenReport();
  const deleteCommentMut = useDeleteComment();
  const saveNoteMut = useSaveReportNote();
  const sendEmailMut = useSendFeedbackEmail();
  const createHistMut = useCreateModerationHistory();
  const changeUserStatusMut = useChangeUserStatus();
  const updatePostStatusMut = useUpdatePostStatus();
  const sendNotificationMut = useSendModerationNotification();
  const deleteMessageMut = useDeleteReportedMessage();
  const overrideMut = useModerationOverride();

  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);

  const id = report ? Number(report.id) : 0;

  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: getGetReportQueryKey(id) });
    void qc.invalidateQueries({ queryKey: getGetModerationHistoryQueryKey(id) });
    void qc.invalidateQueries({ queryKey: MODERATION_INTELLIGENCE_KEY(id) });
  }, [qc, id]);

  const invalidateList = useCallback(() => {
    void qc.invalidateQueries({ queryKey: MODERATION_LIST_KEY });
  }, [qc]);

  // Core dismiss (shared by the explicit action and the action-chaining prompt). Navigates back.
  const runDismiss = useCallback(async () => {
    try {
      await runReversible({
        label: 'Report dismissed',
        forward: () => dismissMut.mutateAsync({ id }),
        inverse: () => reopenMut.mutateAsync({ id }),
        onSettled: () => {
          invalidate();
          invalidateList();
        },
      });
      void navigate(-1);
    } catch {
      toast.error('Failed to dismiss report');
    }
  }, [dismissMut, reopenMut, id, invalidate, invalidateList, navigate]);

  // Action-chaining (doc 09 §2.2): after a type-specific action succeeds, offer to dismiss the report.
  const promptDismiss = useCallback(async () => {
    if (!report || report.status === 'DISMISSED') return;
    const { confirmed } = await confirm({
      title: 'Dismiss Report?',
      description: 'You acted on the reported content. Do you also want to dismiss this report?',
      confirmText: 'Dismiss Report',
      variant: 'default',
    });
    if (confirmed) await runDismiss();
  }, [report, confirm, runDismiss]);

  const dismiss = useCallback(async () => {
    const { confirmed } = await confirm({
      title: 'Dismiss Report',
      description: 'Mark this report as completed. It stays in the queue and can be re-reviewed.',
      confirmText: 'Dismiss Report',
      variant: 'default',
    });
    if (confirmed) await runDismiss();
  }, [confirm, runDismiss]);

  const reopen = useCallback(async () => {
    const { confirmed } = await confirm({
      title: 'Re-review Report',
      description: 'Reopen this report and set its status back to pending.',
      confirmText: 'Re-review',
      variant: 'default',
    });
    if (!confirmed) return;
    try {
      await runReversible({
        label: 'Report reopened',
        forward: () => reopenMut.mutateAsync({ id }),
        inverse: () => dismissMut.mutateAsync({ id }),
        onSettled: invalidate,
      });
    } catch {
      toast.error('Failed to reopen report');
    }
  }, [confirm, reopenMut, dismissMut, id, invalidate]);

  const banUser = useCallback(async () => {
    const target = report?.reportedUser;
    if (!target || !target.userId) {
      toast.error('No reported user to ban');
      return;
    }
    const userId = Number(target.userId);
    const { confirmed, reason } = await confirm({
      title: 'Ban User',
      description: `Ban @${target.uname || target.name}? This terminates their account (status TER).`,
      confirmText: 'Ban User',
      variant: 'destructive',
      requireReason: true,
    });
    if (!confirmed) return;
    const banReason = reason?.trim() || 'User banned';
    try {
      await runReversible({
        label: 'User banned',
        // Backend writes Moderation History (User Banned) with reason — no FE dual-write.
        forward: () =>
          changeUserStatusMut.mutateAsync({
            data: { userId, userStatus: USER_STATUS_BAN, reason: banReason },
          }),
        inverse: () =>
          changeUserStatusMut.mutateAsync({
            data: { userId, userStatus: USER_STATUS_UNBAN, reason: 'Undo ban' },
          }),
        onSettled: invalidate,
      });
      await promptDismiss();
    } catch {
      toast.error('Failed to ban user');
    }
  }, [report, confirm, changeUserStatusMut, invalidate, promptDismiss]);

  const unbanUser = useCallback(async () => {
    const target = report?.reportedUser;
    if (!target || !target.userId) {
      toast.error('No reported user to unban');
      return;
    }
    const userId = Number(target.userId);
    const { confirmed, reason } = await confirm({
      title: 'Unban User',
      description: `Reinstate @${target.uname || target.name}? This reactivates their account (status ACT).`,
      confirmText: 'Unban User',
      variant: 'default',
    });
    if (!confirmed) return;
    const unbanReason = reason?.trim() || 'User unbanned';
    try {
      await runReversible({
        label: 'User unbanned',
        forward: () =>
          changeUserStatusMut.mutateAsync({
            data: { userId, userStatus: USER_STATUS_UNBAN, reason: unbanReason },
          }),
        inverse: () =>
          changeUserStatusMut.mutateAsync({
            data: { userId, userStatus: USER_STATUS_BAN, reason: 'Undo unban' },
          }),
        onSettled: invalidate,
      });
    } catch {
      toast.error('Failed to unban user');
    }
  }, [report, confirm, changeUserStatusMut, invalidate]);

  const takeDown = useCallback(async () => {
    if (report?.kind !== 'CONTENT') return;
    const postId = Number(report.content.id);
    if (!postId) {
      toast.error('No content to take down');
      return;
    }
    const { confirmed, reason } = await confirm({
      title: 'Take Down Content',
      description:
        'Hide this content (HID) and close any open Coop Review Console job so Actions taken / queue counts stay correct.',
      confirmText: 'Take Down',
      variant: 'destructive',
      requireReason: true,
    });
    if (!confirmed) return;
    try {
      // Enforcement → tipmi-api decide() + close_coop_case write-back (not raw post_status).
      await overrideMut.mutateAsync({
        reportId: id,
        data: {
          action: 'FORCE_REJECT',
          ...(reason?.trim() ? { reason: reason.trim() } : { reason: 'Admin take down' }),
        },
      });
      toast.success('Content taken down');
      invalidate();
      await promptDismiss();
    } catch {
      toast.error('Failed to take down content');
    }
  }, [report, confirm, overrideMut, id, invalidate, promptDismiss]);

  const listContent = useCallback(async () => {
    if (report?.kind !== 'CONTENT') return;
    const postId = Number(report.content.id);
    if (!postId) {
      toast.error('No content to list');
      return;
    }
    const { confirmed } = await confirm({
      title: 'List Content',
      description:
        'Republish this content (PUB) and resolve the Coop Review Console case as approved.',
      confirmText: 'List Content',
      variant: 'default',
    });
    if (!confirmed) return;
    try {
      await overrideMut.mutateAsync({
        reportId: id,
        data: { action: 'FORCE_APPROVE', reason: 'Admin re-list' },
      });
      toast.success('Content listed');
      invalidate();
      await promptDismiss();
    } catch {
      toast.error('Failed to list content');
    }
  }, [report, confirm, overrideMut, id, invalidate, promptDismiss]);

  const deleteComment = useCallback(async () => {
    if (report?.kind !== 'COMMENT') return;
    const raw = report.comment.reportedCommentId ?? report.comment.tree?.id ?? null;
    const commentId = raw == null ? NaN : Number(raw);
    if (!commentId || Number.isNaN(commentId)) {
      toast.error('No comment to delete');
      return;
    }
    const { confirmed } = await confirm({
      title: 'Delete Comment',
      description: 'Permanently delete this comment and all of its replies. This cannot be undone.',
      confirmText: 'Delete Comment',
      variant: 'destructive',
      requireReason: true,
    });
    if (!confirmed) return;
    try {
      // Destructive — real DELETE /comment/{id}; backend writes history server-side (no FE createHist).
      await deleteCommentMut.mutateAsync({ id: commentId });
      toast.success('Comment deleted');
      invalidate();
      await promptDismiss();
    } catch {
      toast.error('Failed to delete comment');
    }
  }, [report, confirm, deleteCommentMut, invalidate, promptDismiss]);

  const sendFeedbackEmail = useCallback(async () => {
    const { confirmed, reason } = await confirm({
      title: 'Send Email to Feedbacker',
      description: 'Send an email reply to the reporter. A message is required.',
      confirmText: 'Send Email',
      variant: 'default',
      requireReason: true,
    });
    if (!confirmed) return;
    const message = reason?.trim() ?? '';
    if (!message) {
      toast.error('Message must not be blank');
      return;
    }
    try {
      await sendEmailMut.mutateAsync({ id, data: { message } });
      toast.success('Email sent');
      invalidate();
    } catch {
      toast.error('Failed to send email');
    }
  }, [confirm, sendEmailMut, id, invalidate]);

  const solveFeedback = useCallback(async () => {
    const { confirmed } = await confirm({
      title: 'Solve Feedback',
      description:
        'Mark this feedback as solved. Note: solving currently dismisses the report and any note is not persisted server-side (doc 09 §8 risk #3).',
      confirmText: 'Solve Feedback',
      variant: 'default',
    });
    if (!confirmed) return;
    try {
      // Aliased to dismiss on the backend — reversible via reopen.
      await runReversible({
        label: 'Feedback solved',
        forward: () => dismissMut.mutateAsync({ id }),
        inverse: () => reopenMut.mutateAsync({ id }),
        onSettled: () => {
          invalidate();
          invalidateList();
        },
      });
      void navigate(-1);
    } catch {
      toast.error('Failed to solve feedback');
    }
  }, [confirm, dismissMut, reopenMut, id, invalidate, invalidateList, navigate]);

  const saveNote = useCallback(
    async (note: string) => {
      const trimmed = note.trim();
      if (!trimmed) return; // only save when non-blank (parity :404)
      try {
        await saveNoteMut.mutateAsync({ data: { reportId: id, note: trimmed } });
        toast.success('Note saved');
        invalidate(); // backend auto-creates the note-history entry -> timeline reloads
      } catch {
        toast.error('Failed to save note');
      }
    },
    [saveNoteMut, id, invalidate],
  );

  // Destructive, real hard-delete (Task C): confirm + required reason, no undo, navigate back to the
  // queue on success (the reported thing is gone — the detail page has nothing left to show).
  const removeContent = useCallback(async () => {
    if (report?.kind !== 'CONTENT') return;
    const postId = Number(report.content.id);
    if (!postId) {
      toast.error('No content to remove');
      return;
    }
    const { confirmed, reason } = await confirm({
      title: 'Remove Content',
      description: 'Permanently remove this content from the platform. This cannot be undone.',
      confirmText: 'Remove Content',
      variant: 'destructive',
      requireReason: true,
    });
    if (!confirmed) return;
    const removeReason = reason?.trim() || 'Content removed';
    try {
      // Backend records Content Terminated with reason — no FE dual-write.
      await updatePostStatusMut.mutateAsync({
        postId,
        params: { postStatus: CONTENT_STATUS_REMOVED, reason: removeReason },
      });
      toast.success('Content removed');
      invalidateList();
      void navigate(-1);
    } catch {
      toast.error('Failed to remove content');
    }
  }, [report, confirm, updatePostStatusMut, invalidateList, navigate]);

  const removeMessage = useCallback(async () => {
    if (report?.kind !== 'MESSAGE') return;
    const { confirmed, reason } = await confirm({
      title: 'Remove Message',
      description: 'Permanently remove this message. This cannot be undone.',
      confirmText: 'Remove Message',
      variant: 'destructive',
      requireReason: true,
    });
    if (!confirmed) return;
    const description = reason?.trim() || 'Message removed';
    try {
      await deleteMessageMut.mutateAsync(id);
      await createHistMut.mutateAsync({ data: { reportId: id, action: 'Message Removed', description } });
      toast.success('Message removed');
      invalidateList();
      void navigate(-1);
    } catch {
      toast.error('Failed to remove message');
    }
  }, [report, confirm, deleteMessageMut, createHistMut, id, invalidateList, navigate]);

  // Send Notification (Task B): the action button just opens the dialog; `submitNotification` (below)
  // does the real POST + writes a moderation history entry (backend doesn't auto-log this action,
  // same convention as ban/unban's `afterForward`).
  const sendNotification = useCallback(() => {
    if (!report?.reportedUser?.userId) {
      toast.error('No reported user to notify');
      return Promise.resolve();
    }
    setNotifyDialogOpen(true);
    return Promise.resolve();
  }, [report]);

  const closeNotifyDialog = useCallback(() => setNotifyDialogOpen(false), []);

  const submitNotification = useCallback(
    async (title: string, message: string) => {
      const target = report?.reportedUser;
      if (!target?.userId) {
        toast.error('No reported user to notify');
        return;
      }
      try {
        await sendNotificationMut.mutateAsync({ userId: Number(target.userId), title, message });
        await createHistMut.mutateAsync({ data: { reportId: id, action: 'Notification Sent', description: message } });
        toast.success('Notification sent');
        setNotifyDialogOpen(false);
        invalidate();
      } catch {
        toast.error('Failed to send notification');
      }
    },
    [report, sendNotificationMut, createHistMut, id, invalidate],
  );

  // --- Coop intelligence override actions (Phase 1) ---

  const forceApprove = useCallback(async () => {
    const { confirmed } = await confirm({
      title: 'Force Approve',
      description: 'Override the Coop verdict and force-approve this content. This bypasses all automated checks.',
      confirmText: 'Force Approve',
      variant: 'default',
    });
    if (!confirmed) return;
    try {
      await overrideMut.mutateAsync({ reportId: id, data: { action: 'FORCE_APPROVE' } });
      toast.success('Content force-approved');
      invalidate();
    } catch {
      toast.error('Failed to force approve');
    }
  }, [confirm, overrideMut, id, invalidate]);

  const forceReject = useCallback(async () => {
    const { confirmed, reason } = await confirm({
      title: 'Force Reject',
      description: 'Override the Coop verdict and force-reject this content. This bypasses all automated checks.',
      confirmText: 'Force Reject',
      variant: 'destructive',
      requireReason: true,
    });
    if (!confirmed) return;
    try {
      await overrideMut.mutateAsync({ reportId: id, data: { action: 'FORCE_REJECT', ...(reason?.trim() ? { reason: reason.trim() } : {}) } });
      toast.success('Content force-rejected');
      invalidate();
    } catch {
      toast.error('Failed to force reject');
    }
  }, [confirm, overrideMut, id, invalidate]);

  const escalateGpu = useCallback(async () => {
    const { confirmed } = await confirm({
      title: 'Escalate to GPU',
      description: 'Escalate this case for GPU-accelerated analysis. The case will be re-evaluated with additional compute resources.',
      confirmText: 'Escalate to GPU',
      variant: 'default',
    });
    if (!confirmed) return;
    try {
      await overrideMut.mutateAsync({ reportId: id, data: { action: 'ESCALATE_GPU' } });
      toast.success('Escalated to GPU');
      invalidate();
    } catch {
      toast.error('Failed to escalate to GPU');
    }
  }, [confirm, overrideMut, id, invalidate]);

  const escalateHuman = useCallback(async () => {
    const { confirmed } = await confirm({
      title: 'Escalate to Human Review',
      description: 'Escalate this case for manual human review. A human moderator will evaluate the content.',
      confirmText: 'Escalate to Human',
      variant: 'default',
    });
    if (!confirmed) return;
    try {
      await overrideMut.mutateAsync({ reportId: id, data: { action: 'ESCALATE_HUMAN' } });
      toast.success('Escalated to human review');
      invalidate();
    } catch {
      toast.error('Failed to escalate to human review');
    }
  }, [confirm, overrideMut, id, invalidate]);

  const isPending =
    dismissMut.isPending ||
    reopenMut.isPending ||
    deleteCommentMut.isPending ||
    saveNoteMut.isPending ||
    sendEmailMut.isPending ||
    createHistMut.isPending ||
    changeUserStatusMut.isPending ||
    updatePostStatusMut.isPending ||
    sendNotificationMut.isPending ||
    deleteMessageMut.isPending ||
    overrideMut.isPending;

  return {
    isPending,
    dismiss,
    reopen,
    banUser,
    unbanUser,
    takeDown,
    listContent,
    deleteComment,
    sendFeedbackEmail,
    solveFeedback,
    saveNote,
    removeMessage,
    removeContent,
    sendNotification,
    notifyDialogOpen,
    closeNotifyDialog,
    submitNotification,
    forceApprove,
    forceReject,
    escalateGpu,
    escalateHuman,
  };
}
